import type { InfiniteData } from "@tanstack/react-query";
import type { ConversationDto, MessageStatusDto } from "../contract/chat.events";
import type { ConversationFilter } from "./queryKeys";
import { maxSeq } from "./seq";
import type { ConversationPage } from "./types";

export type ConversationsData = InfiniteData<ConversationPage>;

/** CONTRACT §2.2 order: updatedAt DESC, id DESC. Negative → `a` goes first. */
export function compareConversations(a: ConversationDto, b: ConversationDto): number {
  if (a.updatedAt !== b.updatedAt) return a.updatedAt > b.updatedAt ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id > b.id ? -1 : 1;
}

export function belongsToFilter(c: ConversationDto, filter: ConversationFilter): boolean {
  return filter === "all" || c.unreadCount > 0;
}

export function findConversation(data: ConversationsData | undefined, id: string): ConversationDto | null {
  if (!data) return null;
  for (const page of data.pages) {
    const c = page.items.find((x) => x.id === id);
    if (c) return c;
  }
  return null;
}

/**
 * `conversation:updated`: take the conversation out of wherever it was, then
 * put it back at its sorted place (the unread filter drops read ones). If it
 * sorts after everything loaded while more pages exist, it is left for
 * pagination to bring.
 */
export function upsertConversation(
  data: ConversationsData | undefined,
  conv: ConversationDto,
  filter: ConversationFilter,
): ConversationsData | undefined {
  if (!data) return data;

  const pages = data.pages.map((page) =>
    page.items.some((c) => c.id === conv.id) ? { ...page, items: page.items.filter((c) => c.id !== conv.id) } : page,
  );

  if (belongsToFilter(conv, filter)) {
    let placed = false;
    for (let p = 0; p < pages.length && !placed; p++) {
      const items = pages[p].items;
      const i = items.findIndex((c) => compareConversations(conv, c) < 0);
      if (i !== -1) {
        pages[p] = { ...pages[p], items: [...items.slice(0, i), conv, ...items.slice(i)] };
        placed = true;
      }
    }
    const last = pages.length - 1;
    if (!placed && last >= 0 && pages[last].nextCursor === null) {
      pages[last] = { ...pages[last], items: [...pages[last].items, conv] };
    }
  }

  return { ...data, pages };
}

export function patchConversations(
  data: ConversationsData | undefined,
  id: string,
  fn: (c: ConversationDto) => ConversationDto,
): ConversationsData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    const i = page.items.findIndex((c) => c.id === id);
    if (i === -1) return page;
    const next = fn(page.items[i]);
    if (next === page.items[i]) return page;
    changed = true;
    const items = page.items.slice();
    items[i] = next;
    return { ...page, items };
  });
  return changed ? { ...data, pages } : data;
}

/**
 * `message:status`: `userId` says whose watermarks these are. Mine → my read
 * position; the other side's → ticks. Watermarks only move forward.
 */
export function applyStatus(c: ConversationDto, status: MessageStatusDto, myId: number | null): ConversationDto {
  if (status.userId === myId) {
    const myLastReadSeq = maxSeq(c.myLastReadSeq, status.readUpToSeq);
    return myLastReadSeq === c.myLastReadSeq ? c : { ...c, myLastReadSeq };
  }
  const counterpartLastReadSeq = maxSeq(c.counterpartLastReadSeq, status.readUpToSeq);
  const counterpartLastDeliveredSeq = maxSeq(
    maxSeq(c.counterpartLastDeliveredSeq, status.deliveredUpToSeq),
    counterpartLastReadSeq,
  );
  if (
    counterpartLastReadSeq === c.counterpartLastReadSeq &&
    counterpartLastDeliveredSeq === c.counterpartLastDeliveredSeq
  ) {
    return c;
  }
  return { ...c, counterpartLastReadSeq, counterpartLastDeliveredSeq };
}
